import React, { useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";

const FAQContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  text-align: center;
  margin-bottom: 2rem;
  color: var(--text-color);
`;

const PageSubtitle = styled.p`
  text-align: center;
  font-size: 1.2rem;
  color: var(--text-secondary);
  margin-bottom: 3rem;
`;

const FAQSection = styled.div`
  margin-bottom: 3rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.8rem;
  margin-bottom: 1.5rem;
  color: var(--text-color);
  border-bottom: 2px solid var(--primary-color);
  padding-bottom: 0.5rem;
`;

const FAQItem = styled.div`
  background: var(--card-background);
  border-radius: 10px;
  margin-bottom: 1rem;
  border: 1px solid var(--border-color);
  overflow: hidden;
`;

const FAQQuestion = styled.button<{ $isOpen: boolean }>`
  width: 100%;
  padding: 1.5rem;
  background: none;
  border: none;
  text-align: left;
  cursor: pointer;
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--text-color);
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: background-color 0.3s ease;

  &:hover {
    background: var(--border-color);
  }

  &::after {
    content: "${(props) => (props.$isOpen ? "−" : "+")}";
    font-size: 1.5rem;
    font-weight: bold;
    color: var(--primary-color);
  }
`;

const FAQAnswer = styled.div<{ $isOpen: boolean }>`
  padding: ${(props) => (props.$isOpen ? "1.5rem" : "0 1.5rem")};
  max-height: ${(props) => (props.$isOpen ? "500px" : "0")};
  overflow: hidden;
  transition: all 0.3s ease;
  border-top: ${(props) =>
    props.$isOpen ? "1px solid var(--border-color)" : "none"};
  color: var(--text-secondary);
  line-height: 1.6;
`;

const Link = styled.a`
  color: var(--primary-color);
  text-decoration: none;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
  }
`;

interface FAQData {
  id: string;
  questionKey: string;
  answerKey: string;
}

const faqData: { [key: string]: FAQData[] } = {
  aboutNewme: [
    {
      id: "what-is-newme",
      questionKey: "faq.questions.whatIsNewme.question",
      answerKey: "faq.questions.whatIsNewme.answer",
    },
    {
      id: "why-redesign",
      questionKey: "faq.questions.whyRedesign.question",
      answerKey: "faq.questions.whyRedesign.answer",
    },
    {
      id: "self-determination",
      questionKey: "faq.questions.selfDetermination.question",
      answerKey: "faq.questions.selfDetermination.answer",
    },
  ],
  usingPlatform: [
    {
      id: "how-to-create-map",
      questionKey: "faq.questions.howToCreateMap.question",
      answerKey: "faq.questions.howToCreateMap.answer",
    },
    {
      id: "drawing-tools",
      questionKey: "faq.questions.drawingTools.question",
      answerKey: "faq.questions.drawingTools.answer",
    },
    {
      id: "sharing-maps",
      questionKey: "faq.questions.sharingMaps.question",
      answerKey: "faq.questions.sharingMaps.answer",
    },
  ],
  communityFeatures: [
    {
      id: "commenting-system",
      questionKey: "faq.questions.commentingSystem.question",
      answerKey: "faq.questions.commentingSystem.answer",
    },
    {
      id: "user-authentication",
      questionKey: "faq.questions.userAuthentication.question",
      answerKey: "faq.questions.userAuthentication.answer",
    },
    {
      id: "liking-maps",
      questionKey: "faq.questions.likingMaps.question",
      answerKey: "faq.questions.likingMaps.answer",
    },
    {
      id: "approval-process",
      questionKey: "faq.questions.approvalProcess.question",
      answerKey: "faq.questions.approvalProcess.answer",
    },
    {
      id: "community-guidelines",
      questionKey: "faq.questions.communityGuidelines.question",
      answerKey: "faq.questions.communityGuidelines.answer",
    },
  ],
  technicalQuestions: [
    {
      id: "browser-support",
      questionKey: "faq.questions.browserSupport.question",
      answerKey: "faq.questions.browserSupport.answer",
    },
    {
      id: "mobile-support",
      questionKey: "faq.questions.mobileSupport.question",
      answerKey: "faq.questions.mobileSupport.answer",
    },
    {
      id: "data-privacy",
      questionKey: "faq.questions.dataPrivacy.question",
      answerKey: "faq.questions.dataPrivacy.answer",
    },
  ],
  accountLimits: [
    {
      id: "account-creation",
      questionKey: "faq.questions.accountCreation.question",
      answerKey: "faq.questions.accountCreation.answer",
    },
    {
      id: "map-limits",
      questionKey: "faq.questions.mapLimits.question",
      answerKey: "faq.questions.mapLimits.answer",
    },
    {
      id: "deleting-maps",
      questionKey: "faq.questions.deletingMaps.question",
      answerKey: "faq.questions.deletingMaps.answer",
    },
  ],
};

const FAQPage: React.FC = () => {
  const { t } = useTranslation();
  const [openItems, setOpenItems] = useState<Set<string>>(new Set());

  const toggleItem = (id: string) => {
    const newOpenItems = new Set(openItems);
    if (newOpenItems.has(id)) {
      newOpenItems.delete(id);
    } else {
      newOpenItems.add(id);
    }
    setOpenItems(newOpenItems);
  };

  const sectionTitleMap: { [key: string]: string } = {
    aboutNewme: "faq.sections.aboutNewme",
    usingPlatform: "faq.sections.usingPlatform",
    communityFeatures: "faq.sections.communityFeatures",
    technicalQuestions: "faq.sections.technicalQuestions",
    accountLimits: "faq.sections.accountLimits",
  };

  return (
    <FAQContainer>
      <PageTitle>{t("faq.title")}</PageTitle>
      <PageSubtitle>{t("faq.subtitle")}</PageSubtitle>

      {Object.entries(faqData).map(([sectionKey, items]) => (
        <FAQSection key={sectionKey}>
          <SectionTitle>{t(sectionTitleMap[sectionKey])}</SectionTitle>
          {items.map((item) => (
            <FAQItem key={item.id}>
              <FAQQuestion
                $isOpen={openItems.has(item.id)}
                onClick={() => toggleItem(item.id)}
              >
                {t(item.questionKey)}
              </FAQQuestion>
              <FAQAnswer $isOpen={openItems.has(item.id)}>
                {t(item.answerKey)}
              </FAQAnswer>
            </FAQItem>
          ))}
        </FAQSection>
      ))}

      <FAQSection>
        <SectionTitle>{t("faq.stillHaveQuestions")}</SectionTitle>
        <p style={{ color: "var(--text-secondary)", lineHeight: "1.6" }}>
          {t("faq.stillHaveQuestionsText", {
            contactLink: <Link href="/contact">{t("navigation.contact")}</Link>,
          })}
        </p>
      </FAQSection>
    </FAQContainer>
  );
};

export default FAQPage;
